import { constants } from '@eti/utils';
import { useTranslation } from '@eti/providers';
import type {
  Maybe,
  PersonalItemTravelerDetails,
  PersonalItemTripSummary,
} from '@eti/schema-types';
import {
  INCLUDED_PERSONAL_ITEM,
  INFANT_NO_PERSONAL_ITEM,
  NO_PERSONAL_ITEM,
} from '../../../constants/includedBaggageConstants';
import { resolveTravelerLabel } from '../utils/baggage';
import type {
  TransformedIncludedOrUnavailablePersonalItemType,
  TransformedCartTravelerPriceType,
} from '../../../common/types/transformations/models';
import useNameFormatter from '../../../common/custom-hooks/useNameFormatter';

const { INFANT } = constants.ageTypes;

export const checkSummariesFor = (
  status: string,
  summaries: Array<Maybe<PersonalItemTravelerDetails>> | null = [],
) => summaries?.some((traveler) => traveler?.personalItemStatus === status);

const useIncludedOrUnavailablePersonalItem = (
  personalItemTripSummary: PersonalItemTripSummary,
  travelerPrices: TransformedCartTravelerPriceType[] = [],
): TransformedIncludedOrUnavailablePersonalItemType[] | null => {
  const { t }: { t: (key: any, ...params: any[]) => string } = useTranslation();
  const { formatName } = useNameFormatter();

  const isPersonalItemIncludedOnTrip = checkSummariesFor(
    INCLUDED_PERSONAL_ITEM,
    personalItemTripSummary?.personalItemTravelerDetails,
  );
  const isPersonalItemNotIncludedOnTrip = checkSummariesFor(
    NO_PERSONAL_ITEM,
    personalItemTripSummary?.personalItemTravelerDetails,
  );

  if (isPersonalItemNotIncludedOnTrip) {
    return [
      {
        label: t('Cart.PersonalItem.NotIncluded'),
      },
    ];
  }

  if (!isPersonalItemIncludedOnTrip) {
    return null;
  }

  const infantOnTrip = checkSummariesFor(
    INFANT_NO_PERSONAL_ITEM,
    personalItemTripSummary?.personalItemTravelerDetails,
  );
  return infantOnTrip
    ? travelerPrices.map((traveler) => ({
        label: resolveTravelerLabel(
          t,
          traveler.travelerId,
          formatName(traveler.firstName, traveler.lastName, traveler.fullName),
        ),
        value:
          traveler.ageType === INFANT
            ? t('Cart.Baggage.ContactAirline')
            : t('Cart.PersonalItem.Included'),
      }))
    : [
        {
          label: t('Cart.PersonalItem.IncludedForAll'),
        },
      ];
};

export default useIncludedOrUnavailablePersonalItem;
